import React, { useContext, useMemo, useState } from "react";
import { truncateHTML } from "../../../helpers/truncateHTML.ts";
import TranslationIndicator from "@/components/shared/TranslationIndicator.tsx";
import classNames from "classnames";
import ActivityPrintViewContext from "../ActivityPrintViewContext.ts";

// The hidden print copy is display:none on screen, but browsers still load
// iframes and non-lazy images in it. Lazy ones wait until they become visible
// (printing); the "Druckansicht" flow switches the images to eager first.
function withLazyEmbeds(html: string) {
  const template = document.createElement("template");
  template.innerHTML = html;
  template.content
    .querySelectorAll("img, iframe")
    .forEach((element) => element.setAttribute("loading", "lazy"));
  return template.innerHTML;
}

export default function TruncateableContent({
  content,
  truncateLength = 600,
  translated,
  showMoreButton = true,
  className,
}: {
  content?: string;
  truncateLength?: number;
  translated?: boolean;
  showMoreButton?: boolean;
  className?: string;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isPrintView = useContext(ActivityPrintViewContext);

  const textContent = content?.replace(/<[^>]*>/g, "");
  const shouldTruncate = textContent && textContent.length > truncateLength;

  const isTruncated = !!shouldTruncate && !isExpanded && !!content;
  // Only the print view carries a hidden full copy for print: in the stream it
  // would mount every truncated item's full HTML (incl. iframes and external
  // images, which load even while hidden) into the live DOM.
  const hasPrintCopy = isTruncated && isPrintView;

  const printContent = useMemo(
    () => (hasPrintCopy && content ? withLazyEmbeds(content) : undefined),
    [hasPrintCopy, content],
  );

  const displayContent =
    isTruncated && content
      ? truncateHTML(content, { length: truncateLength })
      : content;

  const handleToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsExpanded(!isExpanded);
  };
  return (
    <>
      {displayContent && (
        <div
          className={classNames(className, { "print:hidden": hasPrintCopy })}
          dangerouslySetInnerHTML={{ __html: displayContent }}
        />
      )}
      {/* On the print view the teaser is screen-only: print the full content. */}
      {printContent && (
        <div
          className={classNames(className, "hidden print:block")}
          dangerouslySetInnerHTML={{ __html: printContent }}
        />
      )}
      {isTruncated && showMoreButton && (
        <>
          {!window.isApp && <span className="print:hidden">…</span>}
          <button
            className={classNames("print:hidden", {
              "btn btn-light flex w-full justify-center text-xl h-11":
                window.isApp,
              "text-primary hover:underline": !window.isApp,
            })}
            type="button"
            onClick={handleToggle}
          >
            {I18n.t(
              window.isApp
                ? "js.activitystreams.mobile.more"
                : "js.activitystreams.more",
            )}
          </button>
        </>
      )}
      {translated && <TranslationIndicator />}
    </>
  );
}
